/**
 * Product Carousel widget — Swiper-powered slider using the same
 * .product-card markup/behaviour as the Product Grid widget & shop archive.
 */

import { initProductGrid } from './product-grid.js';
import { initButtonAnimations } from '../../animations/buttons.js';

const SWIPER_CSS_URL = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
const SWIPER_JS_URL  = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

let swiperAssetsPromise;

export function initProductCarousel() {
	const carousels = [ ...document.querySelectorAll( '.product-carousel' ) ];
	if ( ! carousels.length ) return;

	const initialize = async ( root ) => {
		if ( root.dataset.carouselInit || root.dataset.carouselLoading ) return;
		root.dataset.carouselLoading = '1';

		try {
			await loadSwiperAssets();
			if ( root.isConnected ) initCarousel( root );
		} finally {
			delete root.dataset.carouselLoading;
		}
	};

	if ( ! ( 'IntersectionObserver' in window ) ) {
		carousels.forEach( initialize );
		return;
	}

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( ! entry.isIntersecting ) return;
				observer.unobserve( entry.target );
				initialize( entry.target );
			} );
		},
		{ rootMargin: '600px 0px' }
	);

	carousels.forEach( ( carousel ) => observer.observe( carousel ) );
}

function initCarousel( root ) {
		if ( root.dataset.carouselInit ) return;
		root.dataset.carouselInit = '1';

		let config = {};
		try {
			config = JSON.parse( root.dataset.carousel || '{}' );
		} catch {
			config = {};
		}

		const swiperEl = root.querySelector( '.product-carousel__swiper' );
		if ( ! swiperEl ) return;

		const prevEl = root.querySelector( '.product-carousel__nav--prev' );
		const nextEl = root.querySelector( '.product-carousel__nav--next' );
		const paginationEl = root.querySelector( '.product-carousel__pagination' );

		new window.Swiper( swiperEl, {
			slidesPerView: config.slidesMobile ?? 1.15,
			spaceBetween: config.spaceBetween ?? 20,
			loop: !! config.loop,
			watchOverflow: true,
			grabCursor: true,
			a11y: { enabled: true },
			autoplay: config.autoplay
				? {
						delay: config.autoplayDelay ?? 4500,
						disableOnInteraction: false,
						pauseOnMouseEnter: true,
				  }
				: false,
			navigation: prevEl && nextEl && config.showArrows
				? { prevEl, nextEl }
				: false,
			pagination: paginationEl && config.showDots
				? { el: paginationEl, clickable: true }
				: false,
			breakpoints: {
				576: { slidesPerView: config.slidesTablet ?? 2.3 },
				1024: { slidesPerView: config.slidesDesktop ?? 4 },
			},
		} );

		// Loop mode clones slide DOM nodes synchronously above — (re)bind
		// swatches / sizes / add-to-cart and the button hover animation on
		// every .product-card now present, originals and clones alike.
		initProductGrid( root );
		initButtonAnimations( root );
}

function loadSwiperAssets() {
	if ( swiperAssetsPromise ) return swiperAssetsPromise;

	swiperAssetsPromise = Promise.all( [
		loadStylesheet( SWIPER_CSS_URL, 'theme-swiper-css' ),
		loadScript( SWIPER_JS_URL, 'theme-swiper-js' ),
	] );

	return swiperAssetsPromise;
}

function loadStylesheet( url, id ) {
	const existing = document.getElementById( id );
	if ( existing ) return Promise.resolve();

	return new Promise( ( resolve, reject ) => {
		const link = document.createElement( 'link' );
		link.id = id;
		link.rel = 'stylesheet';
		link.href = url;
		link.addEventListener( 'load', resolve, { once: true } );
		link.addEventListener( 'error', reject, { once: true } );
		document.head.appendChild( link );
	} );
}

function loadScript( url, id ) {
	if ( window.Swiper ) return Promise.resolve();

	const existing = document.getElementById( id );
	if ( existing ) {
		return new Promise( ( resolve, reject ) => {
			existing.addEventListener( 'load', resolve, { once: true } );
			existing.addEventListener( 'error', reject, { once: true } );
		} );
	}

	return new Promise( ( resolve, reject ) => {
		const script = document.createElement( 'script' );
		script.id = id;
		script.src = url;
		script.async = true;
		script.addEventListener( 'load', resolve, { once: true } );
		script.addEventListener( 'error', reject, { once: true } );
		document.head.appendChild( script );
	} );
}
